import { getFeedParams, PostEntity, PostEntityViewAclEnum } from '@interpals/api-web';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';

import api from 'api';
import { ReduxState } from 'redux/root-reducer';
import { RejectionReason } from 'types/errors';
import { TCreatorTypeOptionsEnum } from 'types/post';
import { createCustomToast } from 'services/toasts';
import { localeCapitalize } from 'utils/i18n';

import { FeedFilters, FeedScreen } from './reducer';
import { selectPostsLastId } from './selectors';

const PAGE_LIMIT = 10;
export const PINNED_POSTS_LIMIT = PAGE_LIMIT;

interface CreatePostParams {
    message: string;
    feed_owner_id: string;
    entity_type: 'group' | 'user';
    view_acl: PostEntityViewAclEnum;
    postTitle?: string;
    photo_ids?: string[];
    language?: string;
    creatorType?: TCreatorTypeOptionsEnum;
    gifUrl?: string;
    screen: FeedScreen;
}

interface EditPostParams {
    id: string;
    view_acl: PostEntityViewAclEnum;
    message?: string;
    feed_owner_id?: string;
    title?: string;
    gif_url?: string;
    photo_ids?: string[];
}

interface DeletePostParams {
    id: string;
    feed_owner_id: string;
}

interface GetPostParams {
    id: string;
    feed_owner_id: string;
}

export const createPost = createAsyncThunk<PostEntity, CreatePostParams>(
    'post/create',
    async ({
        message,
        feed_owner_id,
        entity_type,
        view_acl = 'public',
        postTitle,
        photo_ids,
        language,
        creatorType,
        gifUrl,
    }) => {
        return api.createPost({
            feed_owner_id,
            feed_type: entity_type,
            view_acl,
            message,
            photo_ids: photo_ids?.length ? photo_ids.join(',') : undefined,
            language,
            title: postTitle,
            poster_is_group: creatorType === TCreatorTypeOptionsEnum.GROUP,
            // Giphy URL, allowed only if no photo attachments are specified
            gif_url: !photo_ids?.length ? gifUrl : undefined,
        });
    },
);

export const deletePost = createAsyncThunk<string, DeletePostParams>('post/delete', async ({ id, feed_owner_id }) => {
    await api.deletePost({ id, feed_owner_id }).then(() =>
        createCustomToast(
            'success',
            `${i18next.t('Common.SuccessEntityDeletionCaption', {
                entity: localeCapitalize(i18next.t('Common.Entity.Post')),
            })}`,
        ),
    );

    return id;
});

export const getPost = createAsyncThunk<PostEntity, GetPostParams, { rejectValue: Response }>(
    'post/get',
    async ({ id, feed_owner_id }, { rejectWithValue }) => {
        try {
            return await api.getPost({ id, feed_owner_id, extra: 'user.location' });
        } catch (e) {
            return rejectWithValue(e as Response);
        }
    },
);

export type FeedPostType = getFeedParams['post_type'];

interface GetPostsParams {
    id: string;
    screen: FeedScreen;
    filters?: FeedFilters;
    postType?: FeedPostType;
    ownerOnly?: boolean;
    reset?: boolean;
}

export const getPosts = createAsyncThunk<
    { data: PostEntity[]; hasMore: boolean },
    GetPostsParams,
    { state: ReduxState }
>('posts/get', async ({ id, screen, filters, postType, ownerOnly, reset }, { getState }) => {
    const type: getFeedParams['type'] = screen === 'home' ? (filters?.following ? 'following' : 'global') : screen;

    const { data } = await api.getFeed({
        type,
        owner_id: id,
        post_type: postType,
        owner_only: ownerOnly,
        languages: filters?.languages?.join(','),
        countries: filters?.countries?.join(','),
        post_id_less_than: reset ? undefined : selectPostsLastId(getState(), screen),
        limit: PAGE_LIMIT,
        extra: 'user.location',
    });

    return {
        data,
        hasMore: data.length === PAGE_LIMIT,
    };
});

export const editPost = createAsyncThunk<PostEntity, EditPostParams>(
    'post/edit',
    async ({ id, view_acl, message, feed_owner_id, gif_url, photo_ids }) => {
        return api.updatePost({
            id,
            view_acl,
            message,
            feed_owner_id,
            gif_url,
            photo_ids: photo_ids?.join(','),
            extra: 'user',
        });
    },
);

export const fetchPinnedPosts = createAsyncThunk<PostEntity[], string>('pinnedPost/get', async (groupId) => {
    const { data } = await api.getFeed({
        type: 'group',
        owner_id: groupId,
        post_type: 'pinned',
        extra: 'user.location',
    });

    return data;
});

export const pinPost = createAsyncThunk<
    PostEntity,
    PostEntity,
    { state: ReduxState; rejectValue: { reason: RejectionReason } }
>('post/pin', async (post, { getState, rejectWithValue }) => {
    const {
        posts: { pinnedPosts },
    } = getState();

    if (pinnedPosts.data.length >= PINNED_POSTS_LIMIT) {
        return rejectWithValue({ reason: RejectionReason.MaxPinnedPostsLimitExceeded });
    }

    return await api.updatePost({
        id: post.id,
        view_acl: 'public',
        feed_owner_id: post.feed_owner_id,
        pinned: true,
    });
});

export const unpinPost = createAsyncThunk<PostEntity, PostEntity>('post/unpin', async (post) => {
    return await api.updatePost({
        id: post.id,
        view_acl: 'public',
        feed_owner_id: post.feed_owner_id,
        pinned: false,
    });
});

export const moderationDeletePost = createAction<{ id: string }>('delete/moderation/post');
